import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import InstallationForm from '../../components/portal/InstallationForm';
import { RegisterData } from '../../types';
import { register, validateActivationCode, handlePortalApiError } from '../../api/portal';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = Account, 2 = Installation

  // Account data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [activationCode, setActivationCode] = useState('');

  // Plan data from activation code
  const [maxStrings, setMaxStrings] = useState(4);
  const [planName, setPlanName] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [activeStrings, setActiveStrings] = useState(1);

  // Installation data
  const [installation, setInstallation] = useState({
    latitude: 0,
    longitude: 0,
    azimuth1: undefined as number | undefined,
    tilt1: undefined as number | undefined,
    panelCount1: undefined as number | undefined,
    panelSize1: undefined as number | undefined,
    efficiency1: undefined as number | undefined,
    azimuth2: undefined as number | undefined,
    tilt2: undefined as number | undefined,
    panelCount2: undefined as number | undefined,
    panelSize2: undefined as number | undefined,
    efficiency2: undefined as number | undefined,
    azimuth3: undefined as number | undefined,
    tilt3: undefined as number | undefined,
    panelCount3: undefined as number | undefined,
    panelSize3: undefined as number | undefined,
    efficiency3: undefined as number | undefined,
    azimuth4: undefined as number | undefined,
    tilt4: undefined as number | undefined,
    panelCount4: undefined as number | undefined,
    panelSize4: undefined as number | undefined,
    efficiency4: undefined as number | undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('register.errInvalidEmail');
    }

    if (!password || password.length < 8) {
      newErrors.password = t('register.errPwShort');
    }

    if (password && !/[A-Z]/.test(password)) {
      newErrors.password = t('register.errPwUpper');
    }

    if (password && !/[a-z]/.test(password)) {
      newErrors.password = t('register.errPwLower');
    }

    if (password && !/[0-9]/.test(password)) {
      newErrors.password = t('register.errPwDigit');
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('register.errPwMismatch');
    }

    if (!name || name.length < 2) {
      newErrors.name = t('register.errNameShort');
    }

    if (!activationCode || activationCode.length !== 8) {
      newErrors.activationCode = t('register.errCodeLen');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (
      installation.latitude == null ||
      installation.latitude < -90 ||
      installation.latitude > 90
    ) {
      newErrors.latitude = t('register.errLat');
    }

    if (
      installation.longitude == null ||
      installation.longitude < -180 ||
      installation.longitude > 180
    ) {
      newErrors.longitude = t('register.errLon');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleActivationCodeChange = (value: string) => {
    setActivationCode(value);
    // Reset plan info when code changes
    if (value.length !== 8) {
      setPlanName('');
      setMaxStrings(4);
    }
  };

  const handleActivationCodeBlur = async () => {
    if (activationCode.length === 8 && !validatingCode) {
      setValidatingCode(true);
      try {
        const result = await validateActivationCode(activationCode);
        if (result.valid && result.plan) {
          setMaxStrings(result.plan.maxStrings);
          setPlanName(result.plan.name);
          setActiveStrings(Math.min(1, result.plan.maxStrings));
          // Clear activation code error if it exists
          if (errors.activationCode) {
            setErrors((prev) => ({ ...prev, activationCode: '' }));
          }
        } else {
          setErrors((prev) => ({
            ...prev,
            activationCode: result.message || t('register.errCodeInvalid'),
          }));
        }
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          activationCode: t('register.errCodeCheck'),
        }));
      } finally {
        setValidatingCode(false);
      }
    }
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
      setErrors({});
    }
  };

  const handleInstallationChange = (field: string, value: number | undefined) => {
    setInstallation((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const submitRegistration = async (withInstallation: boolean) => {
    if (withInstallation && !validateStep2()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: RegisterData = {
        email,
        password,
        name,
        activationCode,
        ...(withInstallation ? { installation } : {}),
      };

      const response = await register(data);

      // Save tokens so the client stays logged in
      const { clientStorage } = await import('../../utils/clientStorage');
      clientStorage.setTokens(response.accessToken, response.refreshToken);

      setGeneratedApiKey(response.apiKey.key);
    } catch (err) {
      setError(handlePortalApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitRegistration(true);
  };

  const handleSkipInstallation = async () => {
    await submitRegistration(false);
  };

  // Success screen with API key
  if (generatedApiKey) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('register.accountCreated')}
            </h2>
            <p className="text-gray-600">{t('register.apiKeyGenerated')}</p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-yellow-800 mb-2">{t('register.yourApiKey')}</p>
            <code className="block bg-white border border-yellow-300 rounded px-3 py-2 text-sm font-mono text-gray-800 break-all">
              {generatedApiKey}
            </code>
            <p className="text-xs text-yellow-700 mt-2">{t('register.saveKeyWarning')}</p>
          </div>

          <Button variant="primary" className="w-full" onClick={() => navigate('/portal/login')}>
            {t('register.goToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('register.title')}</h1>
          <p className="text-gray-600">{t('register.subtitle')}</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step === 1 ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
                }`}
              >
                {step === 1 ? '1' : '✓'}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">
                {t('register.stepAccount')}
              </span>
            </div>

            <div className="w-16 h-1 bg-gray-300" />

            <div className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}
              >
                2
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">
                {t('register.stepInstallation')}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {step === 1 ? (
            /* Step 1: Account Data */
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleNextStep();
              }}
            >
              <div className="space-y-4">
                <Input
                  label={t('auth.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={errors.email}
                  required
                />

                <Input
                  label={t('auth.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={errors.password}
                  required
                />

                <Input
                  label={t('register.confirmPassword')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={errors.confirmPassword}
                  required
                />

                <Input
                  label={t('register.name')}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={errors.name}
                  required
                />

                <div>
                  <Input
                    label={t('register.activationCode')}
                    type="text"
                    value={activationCode}
                    onChange={(e) => handleActivationCodeChange(e.target.value)}
                    onBlur={handleActivationCodeBlur}
                    error={errors.activationCode}
                    maxLength={8}
                    required
                    disabled={validatingCode}
                  />
                  {validatingCode && (
                    <p className="mt-1 text-sm text-gray-500">{t('register.checkingCode')}</p>
                  )}
                  {planName && !errors.activationCode && (
                    <p className="mt-1 text-sm text-green-600">
                      {t('register.planInfo', { plan: planName, count: maxStrings })}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-between items-center">
                <Link to="/portal/login" className="text-sm text-blue-600 hover:text-blue-700">
                  {t('register.haveAccount')}
                </Link>
                <Button type="submit" variant="primary">
                  {t('register.next')}
                </Button>
              </div>
            </form>
          ) : (
            /* Step 2: Installation Data */
            <form onSubmit={handleSubmit}>
              <InstallationForm
                data={installation}
                onChange={handleInstallationChange}
                errors={errors}
                maxStrings={maxStrings}
                activeStrings={activeStrings}
                onActiveStringsChange={setActiveStrings}
              />

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="mt-6 flex justify-between items-center">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setStep(1);
                    setErrors({});
                  }}
                  disabled={loading}
                >
                  {t('register.back')}
                </Button>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleSkipInstallation}
                    disabled={loading}
                  >
                    {loading ? t('register.registering') : t('register.skipConfig')}
                  </Button>
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? t('register.registering') : t('register.register')}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
